import { useEffect, useRef } from "react";

interface ReshipConfirmDialogProps {
  reference: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const TITLE_ID = "reship-confirm-title";

/**
 * Conferma "Rifai LDV" su un ordine GIÀ SPEDITO. È un'operazione distruttiva:
 * annulla la spedizione Poleepo esistente (tracking incluso) e ne crea una
 * nuova coi dati/corriere editati. Senza conferma la guardia backend lascia
 * intatti gli ordini spediti (riprenderebbe la vecchia LDV così com'è).
 *
 * Stesso pattern modale di ``CarrierSwitchConfirmDialog`` (role=dialog, overlay,
 * Esc, footer Annulla/Conferma). L'esecuzione + busy/feedback li gestisce il
 * parent, che smonta il dialog alla scelta.
 */
export function ReshipConfirmDialog({ reference, onConfirm, onCancel }: ReshipConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-serif text-[17px] font-medium text-ink">
            Rifare la LDV di un ordine già spedito?
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-3">Operazione distruttiva.</p>
        </header>

        <div className="px-5 py-4 text-[12.5px] text-ink leading-relaxed">
          <p className="mb-1">
            Ordine <span className="font-mono font-medium">{reference}</span>
          </p>
          <p>
            Questo ordine risulta <strong>spedito</strong>. Rifare la LDV{" "}
            <strong>annulla la spedizione Poleepo esistente</strong> (tracking incluso) e ne crea
            una nuova coi dati e il corriere che hai impostato. Procedi solo se il collo non è
            ancora partito.
          </p>
        </div>

        <footer className="border-t border-line px-5 py-3">
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              data-testid="reship-cancel-btn"
              onClick={onCancel}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper"
            >
              Annulla
            </button>
            <button
              type="button"
              data-testid="reship-confirm-btn"
              onClick={onConfirm}
              className="rounded-sm border border-red bg-red px-[14px] py-2 text-[13px] font-medium text-white hover:opacity-90"
            >
              Annulla spedizione e rifai
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
